import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  MessageSquarePlus, 
  StickyNote, 
  Clock, 
  Trash2, 
  User,
  AlertCircle,
  Stethoscope,
  Phone,
  FileText
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { z } from 'zod';

interface PatientNote {
  id: string;
  patient_id: string;
  author_user_id: string;
  note_type: string;
  content: string;
  is_private: boolean;
  created_at: string;
  updated_at: string;
  author_email?: string;
}

interface PatientNotesSectionProps {
  patientId: string;
}

const noteSchema = z.object({
  content: z.string().trim().min(1, "Note cannot be empty").max(5000, "Note must be less than 5000 characters"),
  note_type: z.enum(['general', 'clinical', 'follow_up', 'urgent'])
});

const NOTE_TYPES = [
  { value: 'general', label: 'General', icon: StickyNote, color: 'text-blue-600' },
  { value: 'clinical', label: 'Clinical', icon: Stethoscope, color: 'text-green-600' },
  { value: 'follow_up', label: 'Follow-up', icon: Phone, color: 'text-amber-600' },
  { value: 'urgent', label: 'Urgent', icon: AlertCircle, color: 'text-red-600' }
];

export function PatientNotesSection({ patientId }: PatientNotesSectionProps) {
  const { user } = useAdminAuth();
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<string>('general');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadNotes();
  }, [patientId]);

  const loadNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('patient_notes')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch author emails
      const authorIds = [...new Set((data || []).map(n => n.author_user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, email')
        .in('user_id', authorIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p.email]) || []);
      
      setNotes((data || []).map(note => ({
        ...note,
        author_email: profileMap.get(note.author_user_id) || 'Unknown'
      })));
    } catch (err) {
      console.error('Error loading notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    setError(null);
    
    // Validate input
    const validation = noteSchema.safeParse({ content: newNote, note_type: noteType });
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      return;
    }

    if (!user?.id) {
      setError('You must be logged in to add notes');
      return;
    }

    setSubmitting(true);
    try {
      const { error: insertError } = await supabase
        .from('patient_notes')
        .insert({
          patient_id: patientId,
          author_user_id: user.id,
          note_type: noteType,
          content: validation.data.content
        });

      if (insertError) throw insertError;

      toast.success('Note added successfully');
      setNewNote('');
      setNoteType('general');
      setShowAddForm(false);
      loadNotes();
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      const { error } = await supabase
        .from('patient_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      toast.success('Note deleted');
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note');
    }
  };

  const getNoteTypeDetails = (type: string) => {
    return NOTE_TYPES.find(t => t.value === type) || NOTE_TYPES[0];
  };

  const getAuthorInitials = (email: string) => {
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Clinical Notes</CardTitle>
          </div>
          <Button 
            size="sm" 
            onClick={() => setShowAddForm(!showAddForm)}
            variant={showAddForm ? "outline" : "default"}
          >
            <MessageSquarePlus className="h-4 w-4 mr-2" />
            {showAddForm ? 'Cancel' : 'Add Note'}
          </Button>
        </div>
        <CardDescription>
          Clinical observations and notes for this patient
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Note Form */}
        {showAddForm && (
          <div className="p-4 rounded-lg border border-border bg-muted/30 space-y-4">
            <div className="flex items-center gap-4">
              <Select value={noteType} onValueChange={setNoteType}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className={`h-4 w-4 ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Textarea
              placeholder="Enter clinical note or observation..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={4}
              maxLength={5000}
              className="resize-none"
            />
            
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {newNote.length}/5000 characters
              </div>
              <div className="flex items-center gap-2">
                {error && (
                  <span className="text-sm text-destructive">{error}</span>
                )}
                <Button 
                  onClick={handleAddNote} 
                  disabled={submitting || !newNote.trim()}
                  size="sm"
                >
                  {submitting ? 'Saving...' : 'Save Note'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Notes List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <StickyNote className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No notes recorded yet</p>
            <p className="text-sm">Add your first clinical note above</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map(note => {
              const typeDetails = getNoteTypeDetails(note.note_type);
              const TypeIcon = typeDetails.icon;
              const canDelete = note.author_user_id === user?.id || user?.role === 'super_admin';
              
              return (
                <div
                  key={note.id}
                  className="p-4 rounded-lg border border-border bg-card hover:bg-accent/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="h-8 w-8 mt-0.5">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {getAuthorInitials(note.author_email || '')}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-foreground">
                          {note.author_email}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          <TypeIcon className={`h-3 w-3 mr-1 ${typeDetails.color}`} />
                          {typeDetails.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(parseISO(note.created_at), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                      
                      <p className="text-sm text-foreground whitespace-pre-wrap">
                        {note.content}
                      </p>
                    </div>
                    
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
